import type { DisruptionLink } from '../../../arcticapi/types';

interface LineDisruptionTarget {
  lineId?: string | null;
  operatorCode?: string | null;
  lineName?: string | null;
  href?: string | null;
}

function normalizeWebsiteBaseHref(
  websiteBaseHref: string | null,
): string | null {
  const trimmed = websiteBaseHref?.trim();

  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/\/+$/, '');
}

function parseLineIdFromHref(href: string | null | undefined): string | null {
  if (!href) {
    return null;
  }

  const lineIdMatch = href.match(/\/network\/lines\/([^/?#]+)(?=\/|[?#]|$)/i);

  return lineIdMatch ? decodeURIComponent(lineIdMatch[1]) : null;
}

export function buildStopDisruptionHref(
  websiteBaseHref: string | null,
  atcoCode: string | null | undefined,
): string | null {
  const baseHref = normalizeWebsiteBaseHref(websiteBaseHref);
  const trimmedAtcoCode = atcoCode?.trim();

  if (!baseHref || !trimmedAtcoCode) {
    return null;
  }

  // Website URL structure is currently fixed. See `StopDisruptionsController` in arctic-website-bundle and arctic-website-monorepo
  return `${baseHref}/stops/${encodeURIComponent(trimmedAtcoCode)}/updates`;
}

export function buildStopDisruptionLink(
  websiteBaseHref: string | null,
  atcoCode: string | null | undefined,
  count: number,
): DisruptionLink | null {
  if (count < 1) {
    return null;
  }

  return {
    count,
    href: buildStopDisruptionHref(websiteBaseHref, atcoCode),
    kind: 'stop',
  };
}

export function buildLineDisruptionHref(
  websiteBaseHref: string | null,
  target: LineDisruptionTarget,
): string | null {
  const baseHref = normalizeWebsiteBaseHref(websiteBaseHref);

  if (!baseHref) {
    return null;
  }

  const lineId = target.lineId?.trim() || parseLineIdFromHref(target.href);

  if (lineId) {
    return `${baseHref}/.passenger/lines/${encodeURIComponent(lineId)}#disruptions`;
  }

  return null;
}

export function buildLineDisruptionLink(
  websiteBaseHref: string | null,
  target: LineDisruptionTarget,
  count: number,
): DisruptionLink | null {
  if (count < 1) {
    return null;
  }

  return {
    count,
    href: buildLineDisruptionHref(websiteBaseHref, target),
    kind: 'line',
  };
}

// TODO: update URL when https://passenger.atlassian.net/browse/PT-9315 is done
export function buildDisruptionsOverviewHref(
  websiteBaseHref: string | null,
): string | null {
  const baseHref = normalizeWebsiteBaseHref(websiteBaseHref);

  if (!baseHref) {
    return null;
  }

  return `${baseHref}/service-updates`;
}

export function buildDisruptionsOverviewLink(
  websiteBaseHref: string | null,
  count: number,
): DisruptionLink | null {
  if (count < 1) {
    return null;
  }

  return {
    count,
    href: buildDisruptionsOverviewHref(websiteBaseHref),
    kind: 'overview',
  };
}

export function mergeDisruptionLinks(
  websiteBaseHref: string | null,
  links: Array<DisruptionLink | null | undefined>,
): DisruptionLink | null {
  const activeLinks = links.filter((link): link is DisruptionLink =>
    Boolean(link && link.count > 0),
  );

  if (activeLinks.length === 0) {
    return null;
  }

  const count = activeLinks.reduce((total, link) => total + link.count, 0);
  const firstLink = activeLinks[0];
  const uniqueKinds = new Set(activeLinks.map((link) => link.kind));
  const uniqueHrefs = new Set(activeLinks.map((link) => link.href ?? ''));

  if (uniqueKinds.size === 1 && uniqueHrefs.size === 1) {
    return {
      ...firstLink,
      count,
    };
  }

  return (
    buildDisruptionsOverviewLink(websiteBaseHref, count) ?? {
      count,
      href: null,
      kind: 'overview',
    }
  );
}
